// ContactPage.tsx — Public contact form (#233 Phase 4)
import React, { useState } from 'react';
import {
    Box, Container, Typography, TextField, Button, Alert,
    Card, CardContent, CircularProgress,
    AppBar, Toolbar, IconButton,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { ArrowBack, Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TEAL = '#2D6E6A';
const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev';

const ContactPage: React.FC = () => {
    const navigate = useNavigate();
    const isAuthenticated = !!localStorage.getItem('idToken');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [inquiryType, setInquiryType] = useState<'support' | 'advisory'>('support');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
            setError('All fields are required');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: name.trim(), email: email.trim(), subject: subject.trim(), message: message.trim(), inquiryType }),
            });
            const data = await res.json();
            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.error || 'Failed to send message');
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            <AppBar position="static" sx={{ backgroundColor: '#1a1a2e' }} elevation={0}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" fontWeight={700} color="white">P</Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={700} color="white">Percoro</Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" sx={{ py: 6 }}>
                {success ? (
                    <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', p: 4 }}>
                        <CardContent>
                            <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                                <Send sx={{ color: TEAL, fontSize: 32 }} />
                            </Box>
                            <Typography variant="h5" fontWeight={700} gutterBottom>Message Sent!</Typography>
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                {inquiryType === 'advisory'
                                    ? `Thanks for your interest in Charles Advisory. They'll reach out to you shortly at ${email}.`
                                    : `Thanks for reaching out. We'll get back to you shortly at ${email}.`}
                            </Typography>
                            <Button variant="contained" onClick={() => navigate('/')}
                                sx={{ backgroundColor: TEAL, '&:hover': { backgroundColor: '#009688' } }}>
                                Back to Percoro
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <Card elevation={2} sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h4" fontWeight={700} gutterBottom>Contact Us</Typography>
                            <Typography color="text.secondary" sx={{ mb: 4 }}>
                                Have a question or feedback? We'd love to hear from you.
                            </Typography>

                            {isAuthenticated && (
                                <Alert
                                    severity="info"
                                    sx={{ mb: 3, borderRadius: 2 }}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => navigate('/settings#support')}
                                        >
                                            Go to Support
                                        </Button>
                                    }
                                >
                                    Already have an account? Use <strong>Settings &gt; Support</strong> for faster response and ticket tracking.
                                </Alert>
                            )}

                            {!isAuthenticated && (
                                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                    Already have an account?{' '}
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/login?redirect=/settings%23support')}
                                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600, color: '#2D6E6A' }}
                                    >
                                        Log in and use Settings &gt; Support
                                    </Button>{' '}
                                    for faster response and ticket tracking.
                                </Alert>
                            )}

                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                            <TextField fullWidth label="Your Name" value={name}
                                onChange={e => setName(e.target.value)} sx={{ mb: 2 }} />
                            <TextField fullWidth label="Email Address" type="email" value={email}
                                onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel id="inquiry-type-label">What is this about?</InputLabel>
                                <Select
                                    labelId="inquiry-type-label"
                                    label="What is this about?"
                                    value={inquiryType}
                                    onChange={e => setInquiryType(e.target.value as 'support' | 'advisory')}
                                >
                                    <MenuItem value="support">Percoro Service Inquiry</MenuItem>
                                    <MenuItem value="advisory">AI Advisory Inquiry from Chaadv Group</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField fullWidth label="Subject" value={subject}
                                onChange={e => setSubject(e.target.value)} sx={{ mb: 2 }} />
                            <TextField fullWidth multiline rows={5} label="Message" value={message}
                                onChange={e => setMessage(e.target.value)} sx={{ mb: 3 }} />

                            <Button fullWidth variant="contained" size="large"
                                onClick={handleSubmit}
                                disabled={submitting || !name.trim() || !email.trim() || !subject.trim() || !message.trim()}
                                sx={{ backgroundColor: TEAL, '&:hover': { backgroundColor: '#009688' }, py: 1.5 }}>
                                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </Container>
        </Box>
    );
};

export default ContactPage;